import { type Language, type Marketplace, PLATFORMS, type TrafficCampaignSchema } from '@amzn/journeys-common';
import { startsWith } from 'es-toolkit/compat';
import { getCampaignConfigForSchema } from './get-campaign-config-for-schema';

// Satisfies MCC Hero BRD requirement 3.8
// desc: "I should be required to set a name for the flavor (which will become the Symphony campaign/creative name)."
// link: (https://quip-amazon.com/sFLrAw0AH1kU/MCC-Hero-BRD)

const removeMccFromCampaignName = (campaignName: string) => {
  if (!campaignName) return '';
  if (startsWith(campaignName, 'MCC_')) {
    return `${campaignName}`.replace('MCC_', '');
  }
  return campaignName;
};

export type GenerateFlavorCreativeNameParams = {
  campaignName: string;
  language: Language;
  marketplace: Marketplace;
  platform: PLATFORMS | 'multi';
  campaignSchema: TrafficCampaignSchema;
  variant?: string | null;
};

export const generateFlavorCreativeName = ({
  campaignName,
  language,
  marketplace,
  platform,
  campaignSchema,
  variant,
}: GenerateFlavorCreativeNameParams): string => {
  let namePrefix = 'MP';
  if (platform === PLATFORMS.DESKTOP) namePrefix = 'GW';
  if (platform === PLATFORMS.MOBILE) namePrefix = 'MB';

  // Allow certain campaign types to override the first piece of the creative name
  const campaignConfig = getCampaignConfigForSchema(campaignSchema);

  if (campaignConfig?.publishing?.getCreativeNamePrefixOverride) {
    namePrefix = campaignConfig.publishing.getCreativeNamePrefixOverride({
      platform,
    });
  }

  const marketplacePiece = marketplace.toUpperCase();
  // Language is stored as "en_US" or "fr_CA", so we take the piece before the underscore and uppercase it!
  const languagePiece = language.split('_')[0].toUpperCase();
  const campaignNameWithoutMcc = removeMccFromCampaignName(campaignName);

  let variantPiece = '';
  if (campaignConfig.variants?.getCreativeNameVariantString && variant) {
    variantPiece = `${campaignConfig.variants.getCreativeNameVariantString(variant)}_`;
  }

  return `${namePrefix}_${marketplacePiece}_${languagePiece}_${variantPiece}${campaignNameWithoutMcc}`;
};
